// Copyright 2026, University of Maryland, All Rights Reserved
import { type FC, useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import type { RendererComponentProps } from '@/app-shell/renderer-components'
import { useDatasets } from '@/subsystems/pipelines'
import { fetchKbSchema } from '@/subsystems/knowledge-base/shared/core/knowledgeBase.api'
import type { KbSchema, KnowledgeBaseResponse } from '@/subsystems/knowledge-base/shared/types'
import { NegotiationLayout, type NegotiationKbItem } from '../components/NegotiationLayout'
import {
  extractContractDuplicates,
  extractNegotiationFindings,
  extractTroublesomeClauses,
  extractNegotiationEmail,
} from '../hooks/extractors'
import {
  clauseFindingToAnnotation,
  duplicateFindingToAnnotations,
  negotiationFindingToAnnotation,
  type DocxAnnotation,
} from '../components/DocxViewerSheet'
import { DocxView } from '../components/DocxView'

export const NegotiationRenderer: FC<RendererComponentProps> = ({
  renderer,
  documentId,
  version,
  orgId,
  workspaceId,
  apiClient,
  pipelineId,
  slotData = {},
}) => {
  const { datasets } = useDatasets(orgId, workspaceId)

  const findKbInstanceId = (slotKey: string): string | null => {
    const slot = renderer.slots.find(s => s.key === slotKey && s.type === 'dataset')
    const datasetKey = slot?.datasetKey
    if (!datasetKey) return null
    const def = datasets.find(d => d.key === datasetKey)
    return def?.knowledgeBase ?? null
  }

  const negotiationKbInstanceId = findKbInstanceId('negotiation')
  const clausesKbInstanceId = findKbInstanceId('troublesomeClauses')

  const { items: kbItems, schema: negotiationSchema } = useNegotiationKb(
    orgId, workspaceId, negotiationKbInstanceId, apiClient,
  )
  const { items: troublesomeKbItems, schema: troublesomeSchema } = useNegotiationKb(
    orgId, workspaceId, clausesKbInstanceId, apiClient,
  )

  const findings = extractNegotiationFindings(slotData.negotiation ?? null)
  const troublesomeClauses = extractTroublesomeClauses(slotData.troublesomeClauses ?? null)
  const contractDuplicates = extractContractDuplicates(slotData.contractDuplicates ?? null)
  const negotiationEmail = extractNegotiationEmail(slotData.negotiationEmail ?? null)

  const annotations: DocxAnnotation[] = useMemo(() => {
    const a: DocxAnnotation[] = []
    findings.forEach((f, i) => {
      const ann = negotiationFindingToAnnotation(f, i)
      if (ann) a.push(ann)
    })
    troublesomeClauses.forEach((c, i) => {
      const ann = clauseFindingToAnnotation(c, i)
      if (ann) a.push(ann)
    })
    contractDuplicates.forEach((d, i) => {
      a.push(...duplicateFindingToAnnotations(d, i))
    })
    return a
  }, [findings, troublesomeClauses, contractDuplicates])

  const [tab, setTab] = useState('overview')
  const [previousTab, setPreviousTab] = useState<string | null>(null)
  const [activeAnnotationId, setActiveAnnotationId] = useState<string | null>(null)

  const onOpenInDocument = useCallback((annotationId: string) => {
    setActiveAnnotationId(annotationId)
    setPreviousTab(prev => (tab !== 'document' ? tab : prev))
    setTab('document')
  }, [tab])

  const handleTabChange = useCallback((next: string) => {
    if (next !== 'document') setPreviousTab(null)
    setTab(next)
  }, [])

  return (
    <NegotiationLayout
      findings={findings}
      kbItems={kbItems}
      kbSchema={negotiationSchema}
      troublesomeClauses={troublesomeClauses}
      troublesomeKbItems={troublesomeKbItems}
      troublesomeKbSchema={troublesomeSchema}
      contractDuplicates={contractDuplicates}
      negotiationEmail={negotiationEmail}
      tab={tab}
      onTabChange={handleTabChange}
      onOpenInDocument={onOpenInDocument}
      backFromDocumentTo={tab === 'document' ? (previousTab ?? undefined) : undefined}
      documentView={
        pipelineId ? (
          <DocxView
            apiClient={apiClient}
            orgId={orgId}
            workspaceId={workspaceId}
            documentId={documentId}
            pipelineId={pipelineId}
            version={version ?? 1}
            annotations={annotations}
            activeAnnotationId={activeAnnotationId}
          />
        ) : null
      }
    />
  )
}

function useNegotiationKb(
  orgId: string,
  workspaceId: string,
  kbInstanceId: string | null,
  apiClient: RendererComponentProps['apiClient'],
): { items: NegotiationKbItem[]; schema: KbSchema | null } {
  const kbQuery = useQuery<{ items: NegotiationKbItem[]; schemaId: string | null }>({
    queryKey: ['negotiation-kb', orgId, workspaceId, kbInstanceId],
    enabled: Boolean(kbInstanceId),
    queryFn: async () => {
      const url = `/org/${orgId}/ws/${workspaceId}/knowledge_base/instances/${kbInstanceId}/knowledge-base`
      const { data } = await apiClient.get<KnowledgeBaseResponse | null>(url)
      const items = (data?.items ?? []).map(item => ({
        id: item.id,
        content: item.content,
        fields: item.fields ?? null,
      }))
      items.sort((a, b) => {
        const an = Number(a.id)
        const bn = Number(b.id)
        const aNum = Number.isFinite(an)
        const bNum = Number.isFinite(bn)
        if (aNum && bNum) return an - bn
        if (aNum) return -1
        if (bNum) return 1
        return a.id.localeCompare(b.id)
      })
      return { items, schemaId: data?.settings?.itemSchemaId ?? null }
    },
  })

  const schemaId = kbQuery.data?.schemaId ?? null
  const schemaQuery = useQuery<KbSchema | null>({
    queryKey: ['kb-schema', schemaId],
    enabled: Boolean(schemaId),
    queryFn: () => fetchKbSchema(apiClient, schemaId!),
    staleTime: 5 * 60_000,
  })

  return {
    items: kbQuery.data?.items ?? [],
    schema: schemaQuery.data ?? null,
  }
}
